'use client';

import { useLikes } from '@/components/likes/like.provider';
import { cn } from '@bells/core/lib/utils';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import { AspectRatio } from '@bells/ui/components/aspect-ratio';
import { Button } from '@bells/ui/components/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@bells/ui/components/carousel';
import dynamic from 'next/dynamic';
const VideoPlayer = dynamic(() => import('@bells/ui/components/video-player'), {
  loading: () => <Skeleton className='h-full w-full'></Skeleton>,
  ssr: false,
});

import {
  Dialog,
  DialogContent,
  DialogContentClose,
  DialogTitle,
} from '@bells/ui/components/dialog';
import { Skeleton } from '@bells/ui/components/skeleton';
import { useMediaQuery } from '@bells/ui/hooks/use-media-query';
import { ChevronLeft, Gem, Volume2, VolumeX, X } from 'lucide-react';
import Image from 'next/image';
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { attendantMediaItemPrivateOnly } from '@bells/i18n';

export type GirlPicturesModalProps = {
  data: AttendantSearchSingleResponse;
};
export type GirlPictureModalRef = {
  setOpenWithImage: (index: number) => void;
};
const GirlPicturesModal = forwardRef(
  ({ data }: GirlPicturesModalProps, ref: Ref<GirlPictureModalRef>) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [current, setCurrent] = useState(0);

    useImperativeHandle(ref, () => {
      return {
        setOpenWithImage: (val: number) => {
          setIsOpen(true);
          setCurrent(val);
        },
      };
    }, []);

    const [muted, setMuted] = useState<boolean>(true);
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const { likes, doLike, isLoading, hasLike } = useLikes();
    const [showMuteOverlay, setShowMuteOverlay] = useState(true);
    const muteOverlayTimeout = useRef<NodeJS.Timeout | null>(null);

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTitle className='hidden'></DialogTitle>
        <DialogContent
          withClose={false}
          className='w-viewport m-0 flex aspect-[2/3] h-viewport flex-col items-center justify-center overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-lg sm:rounded-none lg:h-full lg:max-h-[90vh] lg:w-auto lg:max-w-[90vw]'
        >
          <Carousel
            opts={{ loop: true, startIndex: current }}
            className='h-full w-full overflow-hidden lg:rounded-lg'
          >
            <CarouselContent
              refClassName={'h-full w-full '}
              className='m-0 flex h-full w-full'
            >
              {(data.media ?? []).map((image, index) => (
                <CarouselItem
                  className='relative flex h-full w-full flex-col justify-center bg-white p-0'
                  key={index}
                >
                  {image.type == 'image' ? (
                    <Image
                      alt={`Image Background ${(index ?? 0) + 1}`}
                      width={0}
                      height={0}
                      unoptimized
                      fill
                      loading='lazy'
                      className='object-cover'
                      src={image.blurThumbnail ?? image.lg}
                    ></Image>
                  ) : (
                    <div className='absolute h-full w-full bg-black'></div>
                  )}

                  <AspectRatio
                    ratio={2 / 3}
                    className='relative z-[2] h-full w-full'
                  >
                    {image?.type == 'video' &&
                      image.path &&
                      image.isVisible && (
                        <Image
                          placeholder={image.thumbnail ? 'blur' : undefined}
                          blurDataURL={image.thumbnail ?? undefined}
                          alt={`Image Background ${(index ?? 0) + 1}`}
                          width={0}
                          height={0}
                          unoptimized
                          fill
                          loading='lazy'
                          className='object-cover'
                          src={image.thumbnail}
                        ></Image>
                      )}

                    {image?.type == 'image' && image.isVisible && (
                      <Image
                        placeholder={image.blurThumbnail ? 'blur' : undefined}
                        blurDataURL={image.blurThumbnail ?? undefined}
                        alt={`Image ${(index ?? 0) + 1}`}
                        width={0}
                        height={0}
                        unoptimized
                        fill
                        loading='lazy'
                        className='object-fit'
                        src={image.xl}
                      ></Image>
                    )}

                    {image?.type == 'image' && !image.isVisible && (
                      <>
                        <Image
                          alt={`Image ${(index ?? 0) + 1}`}
                          fill
                          unoptimized
                          loading='lazy'
                          placeholder={image.blurThumbnail ? 'blur' : undefined}
                          src={image.xl}
                          blurDataURL={image.blurThumbnail ?? undefined}
                          className='h-full w-full object-cover'
                        ></Image>

                        <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                          <Gem></Gem>
                          {attendantMediaItemPrivateOnly()}
                        </div>
                      </>
                    )}

                    {image?.type == 'video' && !image.isVisible && (
                      <>
                        <Image
                          alt={`Image ${(index ?? 0) + 1}`}
                          fill
                          unoptimized
                          loading='lazy'
                          src={image.thumbnail}
                          className='h-full w-full object-cover'
                        ></Image>

                        <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                          <Gem></Gem>
                          {attendantMediaItemPrivateOnly()}
                        </div>
                      </>
                    )}

                    {image?.type == 'video' &&
                      image.path &&
                      image.isVisible && (
                        <div className='relative z-[2] w-full'>
                          <VideoPlayer
                            loop={true}
                            muted={muted}
                            paused={current != index + 1 || isDesktop}
                            autoPlay={true}
                            showControls={false}
                            poster={image.thumbnail}
                            aspectRatio={'2/3'}
                            src={image.path}
                          ></VideoPlayer>
                          <div
                            className='absolute left-0 top-0 z-[50] flex h-full w-full items-center justify-center'
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setMuted((prev) => !prev);
                              setShowMuteOverlay(true);

                              if (muteOverlayTimeout.current) {
                                clearTimeout(muteOverlayTimeout.current);
                              }

                              muteOverlayTimeout.current = setTimeout(() => {
                                setShowMuteOverlay(false);
                              }, 1000);
                            }}
                          >
                            <div
                              className={cn(
                                'animate-fade rounded-full bg-black/70 p-3 text-white opacity-100 transition-opacity delay-0 duration-500',
                                showMuteOverlay || muted
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            >
                              {muted ? (
                                <VolumeX className='h-6 w-6' />
                              ) : (
                                <Volume2 className='h-6 w-6' />
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                  </AspectRatio>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className='absolute left-0 top-0 z-[4] flex items-center p-4 lg:left-auto lg:right-0'>
            <DialogContentClose asChild>
              <Button
                type='button'
                className={cn('h-8 w-8 border-none [&_svg]:size-6')}
                size='icon'
              >
                <ChevronLeft className='lg:hidden' />
                <X className='hidden lg:block'></X>
                <span className='sr-only'>Close</span>
              </Button>
            </DialogContentClose>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

GirlPicturesModal.displayName = 'GirlPicturesModal';

export default GirlPicturesModal;
